import { io, Socket } from 'socket.io-client';
import { createSignal, onCleanup } from 'solid-js';
import { getToken } from '../stores/authStore';
import { config } from '../config/environment';
import type { Visit, Conversion, DashboardStats } from '../types';

// Socket instance
let socket: Socket | null = null;
let currentTeamId: number | null = null;
let visibilityListenerAttached = false;

// Reactive signals for socket state
const [isConnected, setIsConnected] = createSignal(false);
const [isConnecting, setIsConnecting] = createSignal(false);
const [connectionError, setConnectionError] = createSignal<string | null>(null);

// Helper function to decode JWT and extract team info
function decodeToken(token: string): { team_id?: string; team_code?: string; user_id?: string; id?: string; [key: string]: any } | null {
  try {
    // Remove 'Bearer ' prefix if present
    const actualToken = token.replace('Bearer ', '');
    // Split the token and get the payload
    const parts = actualToken.split('.');
    if (parts.length !== 3) return null;

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    return null;
  }
}

// Initialize socket connection
export const initializeSocket = (teamCode?: string): Socket => {
  if (socket?.connected) {
    return socket;
  }

  const token = getToken();

  // Connect to Socket.io server (proxy configured in vite.config.ts)
  socket = io(config.api.socketUrl, {
    path: '/socket.io',
    auth: {
      token,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
  });

  // Connection handlers
  socket.on('connect_attempt', () => {
    // Only show connecting if not already connected
    if (!socket?.connected) {
      setIsConnecting(true);
    }
  });

  socket.on('connect', () => {
    setIsConnected(true);
    setIsConnecting(false);
    setConnectionError(null);

    // Extract team_id from JWT token and join team-specific room
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        // Use team_id if available, otherwise use a default team ID
        // The token contains user_id in 'id' field or 'user_id' field
        const teamId = decoded.team_id || decoded.team_code || '1';
        currentTeamId = teamId as any;
        const roomName = `team:${teamId}`;
        // Use socket.emit to join the room on the backend
        socket?.emit('join_room', { team_id: teamId, room: roomName });
      }
    }
  });

  socket.on('disconnect', (reason) => {
    setIsConnected(false);
    setIsConnecting(false);
  });

  socket.on('connect_error', (error) => {
    setConnectionError(error.message);
    setIsConnected(false);
    setIsConnecting(false);  // Don't keep showing "connecting" on error
  });

  socket.on('error', (error) => {
    setConnectionError(error);
    setIsConnecting(false);
  });

  // Auto-reconnect on visibility change (user comes back to page) - attach listener only once
  if (typeof document !== 'undefined' && !visibilityListenerAttached) {
    visibilityListenerAttached = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && socket && !socket.connected) {
        console.log('[Socket] Page became visible and socket disconnected, attempting reconnect...');
        socket.connect();
      }
    });
  }

  // Configure unlimited reconnection attempts
  socket.io.opts.reconnection = true;
  socket.io.opts.reconnectionAttempts = Infinity;  // Unlimited reconnection attempts
  socket.io.opts.reconnectionDelay = 1000;
  socket.io.opts.reconnectionDelayMax = 5000;

  return socket;
};

// Disconnect socket
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionError(null);
  }
};

// Join a team room
export const joinRoom = (teamId: string) => {
  if (socket?.connected) {
    socket.emit('join_room', teamId);
  }
};

// Leave a team room
export const leaveRoom = (teamId: string) => {
  if (socket?.connected) {
    socket.emit('leave_room', teamId);
  }
};

// Event listeners
export const onNewClick = (callback: (data: Visit) => void) => {
  if (!socket) return;

  socket.on('new_click', callback);
  socket.on('click-recorded', callback);

  // Return cleanup function
  return () => {
    socket?.off('new_click', callback);
    socket?.off('click-recorded', callback);
  };
};

export const onNewConversion = (callback: (data: Conversion) => void) => {
  if (!socket) return;

  socket.on('new_conversion', callback);
  socket.on('conversion-recorded', callback);

  return () => {
    socket?.off('new_conversion', callback);
    socket?.off('conversion-recorded', callback);
  };
};

export const onStatsUpdate = (callback: (data: DashboardStats) => void) => {
  if (!socket) return;

  socket.on('stats_update', callback);

  return () => {
    socket?.off('stats_update', callback);
  };
};

// Custom event listener
export const on = (event: string, callback: (...args: any[]) => void) => {
  if (!socket) return;

  socket.on(event, callback);

  return () => {
    socket?.off(event, callback);
  };
};

// Emit custom event
export const emit = (event: string, ...args: any[]) => {
  if (socket?.connected) {
    socket.emit(event, ...args);
  }
};

// Get current socket instance
export const getSocket = (): Socket | null => socket;

// Export reactive state
export const socketState = {
  isConnected,
  isConnecting,
  connectionError,
};

// SolidJS hook for socket events
export const useSocketEvent = <T = any>(
  event: string,
  callback: (data: T) => void
): void => {
  // Create a wrapper function to properly handle the callback
  // This ensures we have a stable reference for cleanup
  const handler = (data: T) => {
    callback(data);
  };

  // Register listener immediately or wait for socket
  const registerListener = () => {
    if (socket) {
      socket.on(event, handler);
    } else {
      // Retry after a short delay if socket not ready
      const retryTimeout = setTimeout(registerListener, 100);
      onCleanup(() => clearTimeout(retryTimeout));
    }
  };

  registerListener();

  // Cleanup on component unmount
  onCleanup(() => {
    socket?.off(event, handler);
  });
};

export default {
  initialize: initializeSocket,
  disconnect: disconnectSocket,
  joinRoom,
  leaveRoom,
  onNewClick,
  onNewConversion,
  onStatsUpdate,
  on,
  emit,
  getSocket,
  useSocketEvent,
  state: socketState,
};
